/**
 * QuoteCart Web Component
 * Manages a localStorage-based quote cart for product quotes
 */
class QuoteCart extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'sms_quote_cart';
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  /**
   * Check if two items are identical (same variant and properties)
   * @param {Object} item1 - First item
   * @param {Object} item2 - Second item
   * @returns {boolean} True if items are identical
   */
  areItemsIdentical(item1, item2) {
    // Check if variant IDs match
    if (item1.variantId !== item2.variantId) {
      return false;
    }

    // Check if properties match
    const props1 = item1.properties || {};
    const props2 = item2.properties || {};

    const keys1 = Object.keys(props1).sort();
    const keys2 = Object.keys(props2).sort();

    // Different number of properties
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check each property value
    for (let key of keys1) {
      if (props1[key] !== props2[key]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Add an item to the quote cart
   * @param {Object} item - The item to add
   * @param {string} item.variantId - Shopify variant ID
   * @param {number} item.quantity - Quantity
   * @param {string} item.title - Product title
   * @param {number} item.price - Price in cents
   * @param {Object} item.properties - Configuration properties
   * @param {Object} item.configuration - Full configuration object
   */
  addItem(item) {
    const cart = this.getCart();

    // Check if an identical item already exists
    const existingItem = cart.items.find(cartItem => this.areItemsIdentical(cartItem, item));

    if (existingItem) {
      // Item exists, increase quantity
      existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
      this.saveCart(cart);
      this.dispatchEvent(new CustomEvent('quote-cart-updated', {
        detail: { cart, action: 'update', item: existingItem },
        bubbles: true
      }));
      this.render();
      return existingItem;
    } else {
      // New item, add to cart
      const newItem = {
        id: this.generateItemId(),
        ...item,
        addedAt: new Date().toISOString(),
      };

      cart.items.push(newItem);
      this.saveCart(cart);
      this.dispatchEvent(new CustomEvent('quote-cart-updated', {
        detail: { cart, action: 'add', item: newItem },
        bubbles: true
      }));
      this.render();
      return newItem;
    }
  }

  /**
   * Remove an item from the quote cart
   * @param {string} itemId - The ID of the item to remove
   */
  removeItem(itemId) {
    const cart = this.getCart();
    const itemIndex = cart.items.findIndex(item => item.id === itemId);

    if (itemIndex > -1) {
      const removedItem = cart.items.splice(itemIndex, 1)[0];
      this.saveCart(cart);
      this.dispatchEvent(new CustomEvent('quote-cart-updated', {
        detail: { cart, action: 'remove', item: removedItem },
        bubbles: true
      }));
      this.render();
      return removedItem;
    }
    return null;
  }

  /**
   * Update the quantity of an item
   * @param {string} itemId - The ID of the item to update
   * @param {number} quantity - New quantity
   */
  updateQuantity(itemId, quantity) {
    const cart = this.getCart();
    const item = cart.items.find(item => item.id === itemId);

    if (item) {
      item.quantity = quantity;
      this.saveCart(cart);
      this.dispatchEvent(new CustomEvent('quote-cart-updated', {
        detail: { cart, action: 'update', item },
        bubbles: true
      }));
      this.render();
      return item;
    }
    return null;
  }

  /**
   * Get the current cart
   * @returns {Object} Cart object with items array
   */
  getCart() {
    try {
      const cartData = localStorage.getItem(this.storageKey);
      return cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error reading quote cart:', error);
      return { items: [], createdAt: new Date().toISOString() };
    }
  }

  /**
   * Get all items in the cart
   * @returns {Array} Array of cart items
   */
  getItems() {
    return this.getCart().items;
  }

  /**
   * Get the total number of items in the cart
   * @returns {number} Total quantity of items
   */
  getItemCount() {
    return this.getCart().items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }

  /**
   * Get the total price of all items in the cart
   * @returns {number} Total price in cents
   */
  getTotalPrice() {
    return this.getCart().items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  }

  /**
   * Clear all items from the cart
   */
  clearCart() {
    const cart = { items: [], createdAt: new Date().toISOString() };
    this.saveCart(cart);
    this.dispatchEvent(new CustomEvent('quote-cart-updated', {
      detail: { cart, action: 'clear' },
      bubbles: true
    }));
    this.render();
  }

  /**
   * Save the cart to localStorage
   * @param {Object} cart - Cart object to save
   */
  saveCart(cart) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving quote cart:', error);
    }
  }

  /**
   * Generate a unique ID for cart items
   * @returns {string} Unique ID
   */
  generateItemId() {
    return `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Format money for display
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      console.error('Error formatting money:', error);
      return '$0.00';
    }
  }

  /**
   * Strip price information from property values
   * @param {string} value - Property value that may contain price
   * @returns {string} Value with price information removed
   */
  stripPriceFromValue(value) {
    if (typeof value === 'string') {
      return value.split(' (+')[0].split(' (-')[0];
    }
    return value;
  }

  /**
   * Render the cart UI
   */
  render() {
    const cart = this.getCart();
    const itemCount = this.getItemCount();
    const totalPrice = this.getTotalPrice();

    this.innerHTML = `
      <div class="quote-cart">
        <div class="quote-cart__header">
          <h3 class="quote-cart__title">Quote Cart</h3>
          <span class="quote-cart__count">${itemCount} item${itemCount !== 1 ? 's' : ''}</span>
        </div>

        ${cart.items.length === 0 ? `
          <div class="quote-cart__empty">
            <p>Your quote cart is empty</p>
          </div>
        ` : `
          <div class="quote-cart__items">
            ${cart.items.map(item => this.renderItem(item)).join('')}
          </div>

          <div class="quote-cart__footer">
            <div class="quote-cart__total">
              <span>Total:</span>
              <strong>${this.formatMoney(totalPrice)}</strong>
            </div>
            <button class="quote-cart__clear-btn" data-action="clear">Clear Cart</button>
          </div>
        `}
      </div>
    `;
  }

  /**
   * Render a single cart item
   * @param {Object} item - Cart item
   * @returns {string} HTML string
   */
  renderItem(item) {
    const properties = item.properties || {};
    const propertyList = Object.entries(properties)
      .filter(([key]) => !key.startsWith('_'))
      .map(([key, value]) => `<li><strong>${key}:</strong> ${this.stripPriceFromValue(value)}</li>`)
      .join('');

    // Convert relative image URLs to full CDN URLs
    let imageUrl = item.image || '';
    if (imageUrl && imageUrl.startsWith('files/')) {
      imageUrl = imageUrl.replace('files/', 'https://savemyserver.com/cdn/shop/files/');
    }

    // Build image HTML
    const imageHTML = imageUrl
      ? `<img src="${imageUrl}" alt="${item.title || 'Product'}" style="width: 100%; height: 150px; object-fit: cover; border-radius: 4px 4px 0 0;" loading="lazy">`
      : '';

    return `
      <div class="quote-cart__item" data-item-id="${item.id}">
        ${imageHTML}
        <div class="quote-cart__item-header">
          <h4 class="quote-cart__item-title">${item.title}</h4>
          <button class="quote-cart__item-remove" data-action="remove" data-item-id="${item.id}">
            &times;
          </button>
        </div>

        <div class="quote-cart__item-details">
          ${propertyList ? `
            <ul class="quote-cart__item-properties">
              ${propertyList}
            </ul>
          ` : ''}

          <div class="quote-cart__item-footer">
            <div class="quote-cart__item-quantity">
              Quantity: ${item.quantity || 1}
            </div>
            <div class="quote-cart__item-price">
              ${this.formatMoney(item.price * (item.quantity || 1))}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Setup event listeners for cart interactions
   */
  setupEventListeners() {
    this.addEventListener('click', (e) => {
      const target = e.target;
      const action = target.dataset.action;

      if (action === 'remove') {
        const itemId = target.dataset.itemId;
        if (itemId && confirm('Remove this item from the quote cart?')) {
          this.removeItem(itemId);
        }
      } else if (action === 'clear') {
        if (confirm('Clear all items from the quote cart?')) {
          this.clearCart();
        }
      }
    });
  }
}

// Define the custom element
if (!customElements.get('quote-cart')) {
  customElements.define('quote-cart', QuoteCart);
}
